import { useRef, useCallback, useEffect } from 'react';
import useBreakpoints from './useBreakpoints';
import { useToggleFavorite } from '../mutations';
import { EntityType } from '../@types';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { SKIN_FEATURE, useSkinFeature } from '../config/skinRegistry';

type HoldEventHandlers = {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
  onTouchMove: (e: React.TouchEvent) => void;
};

const useMobileOnHold = (isFavoriteEntity: boolean, entityType: EntityType, entityId?: string) => {
  const { isMobile } = useBreakpoints();
  const { isAuthenticated } = useAuth();
  const { toggleFavorite, isPending } = useToggleFavorite(entityType, entityId);
  const favoritesEnabled = useSkinFeature(SKIN_FEATURE.FAVORITES);

  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startY = useRef<number | null>(null);

  const isChecked = isAuthenticated && isFavoriteEntity;

  const handleFavorite = useCallback(() => {
    if (!isAuthenticated || !favoritesEnabled || !entityId || isPending) return;
    toggleFavorite(entityId, entityType, !isChecked);
  }, [entityType, isChecked, entityId, toggleFavorite, isAuthenticated, favoritesEnabled, isPending]);

  useEffect(() => {
    return () => {
      if (pressTimer.current) {
        clearTimeout(pressTimer.current);
      }
    };
  }, [entityId, entityType, isFavoriteEntity, isAuthenticated, favoritesEnabled, isPending]);

  const startHold = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile || !isAuthenticated || !favoritesEnabled || !entityId || isPending) return;

      e.stopPropagation();
      if (pressTimer.current) clearTimeout(pressTimer.current);
      startY.current = e.touches[0].clientY;
      pressTimer.current = setTimeout(() => handleFavorite(), 600);
    },
    [handleFavorite, isMobile, isAuthenticated, favoritesEnabled, entityId, isPending]
  );

  const cancelHold = useCallback(() => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    startY.current = null;
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      //detect scroll and cancel hold event
      if (startY.current !== null) {
        const deltaY = Math.abs(e.touches[0].clientY - startY.current);
        if (deltaY > 10) {
          // Threshold for detecting scroll
          cancelHold();
        }
      }
    },
    [cancelHold]
  );

  const holdHandlers: HoldEventHandlers = {
    onTouchStart: startHold,
    onTouchEnd: cancelHold,
    onTouchCancel: cancelHold,
    onTouchMove: handleTouchMove,
  };

  return {
    holdHandlers,
    isChecked,
    handleFavorite,
    isPending,
  };
};

export default useMobileOnHold;
